import { useSelector } from '@legendapp/state/react';
import { Box, Button, Center, createStyles, lighten, TFLogo } from '@tf/ui';
import { Link, useLocation } from 'react-router-dom';

import { type ProfileGroups, useProfileGroupsSchemas } from '@/core/api/profiles';
import { useSelectedAccountSafely } from '@/core/hooks';
import { configStore } from '@/core/stores';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

const useStyles = createStyles(({ colors }) => ({
	menu: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
	},
	logo: {
		backgroundColor: '#fff',
		display: 'inline-flex',
		marginRight: '1rem',
		height: 22,
		width: 22,
		'&:hover': {
			backgroundColor: colors.bra,
		},
	},
	menuButton: {
		height: 44,
		borderRadius: 0,
		borderBottomStyle: 'solid',
		borderBottomWidth: 1.5,
		borderBottomColor: 'transparent',
		'&[data-active="true"]': {
			backgroundColor: lighten(colors.brand[0], 0.5),
			borderBottomColor: colors.brand[6],
		},
	},
}));

export const AppMenu = () => {
	const { classes } = useStyles();
	const config = useSelector(() => configStore.get());
	const { pathname } = useLocation();
	const { data: profileGroups } = useProfileGroupsSchemas();

	const withObserver = Boolean(Number(config.feature_flags?.entity_observer));
	const withPaymentsCapability = useCurrentUserCapabilityCheck(UserCapabilities.CAN_REVIEW_PAYMENTS);
	const isMonitoringRules = useCurrentUserCapabilityCheck(UserCapabilities.CAN_MANAGE_MONITORING);

	const menuItems = [];

	if (!profileGroups?.groups) {
		menuItems.push({ label: 'Accounts', path: '/' });
	} else {
		profileGroups.groups.forEach((group: ProfileGroups) => {
			menuItems.push({
				label: group.label,
				path: `/${group.label.toLowerCase()}`,
			});
		});
	}

	menuItems.push({ label: 'Instant check', path: '/instant-check' });

	if (isMonitoringRules) {
		menuItems.push({
			label: 'Monitoring',
			path: '/monitoring',
		});
	}

	if (withObserver) {
		menuItems.push({
			label: 'Entities',
			path: '/entities',
		});
	}

	if (withPaymentsCapability) {
		menuItems.push({
			label: 'Payments',
			path: '/payments',
		});
	}

	const currentAccount = useSelectedAccountSafely();
	const isMenuItemActive = (item: { path: string; label: string }) => {
		const isHomePath = item.path === '/';

		if (isHomePath) {
			return pathname.startsWith('/accounts') || pathname === '/';
		}

		const currentGroup = profileGroups?.groups?.find(
			(group: ProfileGroups) => `/${group.label.toLowerCase()}` === item.path,
		);

		if (currentGroup) {
			const isFirstGroup = profileGroups?.groups?.[0]?.label.toLowerCase() === item.path.substring(1);
			const isHomeForFirstGroup = pathname === '/' && isFirstGroup;
			const isOnAccountRoute = pathname.startsWith('/accounts/');
			const accountMatchesGroup = currentAccount && currentGroup.kinds.includes(currentAccount.meta.entityKind);
			const isAccountInGroup = isOnAccountRoute && accountMatchesGroup;

			return pathname === item.path || isHomeForFirstGroup || isAccountInGroup;
		}

		return pathname.startsWith(item.path);
	};

	return (
		<Box className={classes.menu}>
			<Center component={Link} to="/" h={44}>
				<Box className={classes.logo}>
					<TFLogo size={18} />
				</Box>
			</Center>

			{menuItems.map((item) => (
				<Button
					h="44px"
					key={item.path}
					component={Link}
					to={item.path}
					variant="subtle"
					className={classes.menuButton}
					data-active={isMenuItemActive(item)}
				>
					{item.label}
				</Button>
			))}
		</Box>
	);
};
